export const getScoreColors = (score: number): { color1: string; color2: string } => {
    if (score > 8) return { color1: '#27E757', color2: '#0DAF35' };
    if (score >= 6.5) return { color1: '#86DF11', color2: '#5CAE13' };
    if (score >= 4.5) return { color1: '#F5C60A', color2: '#DA840B' };
    if (score >= 2) return { color1: '#F7774A', color2: '#DF520C' };
    return { color1: '#F5443A', color2: '#C11626' };
};

export const formatScore = (score: number): string => score.toFixed(1).replace(/\.0$/, '');

export const formatFreshness = (days: number): string => {
    if (days < 1) return 'today';
    if (days === 1) return '1 day ago';
    if (days < 7) return `${days} days ago`;
    if (days < 30) {
        const weeks = Math.floor(days / 7);
        return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    }
    if (days < 365) {
        const months = Math.floor(days / 30);
        return months === 1 ? '1 month ago' : `${months} months ago`;
    }

    const years = Math.floor(days / 365);
    return years === 1 ? '1 year ago' : `${years} years ago`;
};

export const formatCost = (relativeFees?: number): { active: string; muted: string } => {
    if (!relativeFees || relativeFees < 1 || relativeFees > 5) {
        return { active: '', muted: '' };
    }

    return {
        active: '\u00A3'.repeat(relativeFees),
        muted: '\u00A3'.repeat(5 - relativeFees)
    };
};

export const parseEventDateTime = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const d = new Date(dateStr.replace(/_/g, '-'));
    return isNaN(d.getTime()) ? null : d;
};

export interface SingleDayEvent {
    event_name: string;
    event_description: string;
    datetime_start: string;
    datetime_end: string;
    contact_name: string;
    contact_phone: string;
    contact_email: string;
    formattedDate: string;
    formattedStartTime: string;
    formattedEndTime: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ordinalSuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

const formatTime = (d: Date): string =>
    `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

export const getSingleDayEvents = (events?: { event_name: string; event_description: string; datetime_start: string; datetime_end: string; contact_name: string; contact_phone: string; contact_email: string }[]): SingleDayEvent[] => {
    if (!events || events.length === 0) return [];

    return events
        .map(ev => {
            const start = parseEventDateTime(ev.datetime_start);
            const end = parseEventDateTime(ev.datetime_end);
            if (!start || !end) return null;
            const durationMs = end.getTime() - start.getTime();
            if (durationMs <= 0 || durationMs >= 24 * 60 * 60 * 1000) return null;
            return {
                event_name: ev.event_name,
                event_description: ev.event_description,
                datetime_start: ev.datetime_start,
                datetime_end: ev.datetime_end,
                contact_name: ev.contact_name,
                contact_phone: ev.contact_phone,
                contact_email: ev.contact_email,
                formattedDate: `${MONTHS[start.getMonth()]} ${start.getDate()}${ordinalSuffix(start.getDate())}`,
                formattedStartTime: formatTime(start),
                formattedEndTime: formatTime(end),
            } as SingleDayEvent;
        })
        .filter((e): e is SingleDayEvent => e !== null)
        .sort((a, b) => {
            const da = parseEventDateTime(a.datetime_start)!;
            const db = parseEventDateTime(b.datetime_start)!;
            return da.getTime() - db.getTime();
        });
};

export const formatInspectionTime = (days?: number | null): string => {
    if (days === null || days === undefined || isNaN(days) || days < 0) return 'No Inspection History';
    if (days < 30) return '1 month';
    if (days < 365) {
        const months = Math.floor(days / 30);
        return months === 1 ? '1 month' : `${months} months`;
    }

    const years = Math.floor(days / 365);
    const remainingDays = days % 365;
    const months = Math.floor(remainingDays / 30);

    let result = years === 1 ? '1 year' : `${years} years`;
    if (months > 0) {
        result += months === 1 ? ', 1 month' : `, ${months} months`;
    }

    return result;
};
